import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, FormFieldSetting } from '../services/api';
import { useTestMode } from '../auth/TestModeContext';
import { useAuth } from '../auth/useAuth';

// ── Types ─────────────────────────────────────────────────────
interface Department {
  DepartmentID:   number;
  DepartmentName: string;
  DirectorName:   string | null;
  DirectorEmail:  string | null;
  AGMName:        string | null;
  AGMEmail:       string | null;
  IsActive:       boolean;
}

interface User {
  UserID:         number;
  Email:          string;
  DisplayName:    string;
  DepartmentName: string | null;
  AppRole:        string | null;
  IsActive:       boolean;
  LastLoginAt:    string | null;
}

interface Project {
  ProjectID:              number;
  Title:                  string;
  DepartmentName:         string;
  RequestorName:          string;
  Priority:               string;
  ApprovalStatus:         string;
  ProjectStatus:          string | null;
  ITNotes:                string | null;
  IsActive:               boolean;
  BusinessOwnerName:      string | null;
  BusinessOwnerEmail:     string | null;
  ProjectManagerName:     string | null;
  ProjectManagerEmail:    string | null;
  ProjectDescription:     string | null;
  Objectives:             string | null;
  Deliverables:           string | null;
  OrganizationalBenefits: string | null;
  Budgeted:               string | null;
  AmountBudgeted:         number | null;
  StartDate:              string | null;
  EndDate:                string | null;
}

interface SpendingAuthority {
  manager:           number;
  assistantDirector: number;
  director:          number;
  agm:               number;
  ceo:               number;
}

interface AppSettings {
  emailNotificationsEnabled: boolean;
  testModeRecipients:        string[];
  ceoName:                   string;
  ceoEmail:                  string;
  spendingAuthority:         SpendingAuthority;
  spendingAuthorityEnabled:  boolean;
}

interface ChainStep {
  StepID:    number;
  StepName:  string;
  StepOrder: number;
  IsEnabled: boolean;
}

interface AdminStats {
  TotalUsers:       number;
  TotalProjects:    number;
  ActiveProjects:   number;
  PendingApprovals: number;
  DraftProjects:    number;
  RevisionProjects: number;
  LastAuditEvent:   string | null;
}

// ── Helpers ───────────────────────────────────────────────────
function formatDateTime(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatCurrency(val: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
}

// ── Toast ─────────────────────────────────────────────────────
function Toast({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, background: type === 'success' ? 'var(--success-bg, #ecfdf5)' : 'var(--danger-bg, #fef2f2)', color: type === 'success' ? 'var(--success-text, #065f46)' : 'var(--danger-text, #991b1b)', border: `1px solid ${type === 'success' ? '#6ee7b7' : '#fca5a5'}`, borderRadius: 10, padding: '12px 18px', fontSize: 13, fontWeight: 500, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxWidth: 360 }}>
      {message}
    </div>
  );
}

function ConfirmDialog({ title, itemName, message, requireTypedConfirmation, onConfirm, onCancel }: { title: string; itemName: string; message: string; requireTypedConfirmation?: boolean; onConfirm: () => void; onCancel: () => void }) {
  const [typed, setTyped] = useState('');
  const locked = !!requireTypedConfirmation && typed.trim() !== itemName.trim();
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, maxWidth: 400, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text1)', marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--danger-text, #991b1b)', marginBottom: 8 }}>{itemName}</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20, lineHeight: 1.5 }}>{message}</div>
        {requireTypedConfirmation && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>
              Type <strong>{itemName}</strong> to confirm:
            </div>
            <input className="field-input" style={{ width: '100%' }} value={typed} onChange={e => setTyped(e.target.value)} autoFocus />
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-sm" onClick={onConfirm} disabled={locked} style={{ background: 'var(--danger, #dc2626)', color: '#fff', border: 'none', opacity: locked ? 0.5 : 1 }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

function ReassignDepartmentDialog({ projectName, departments, onConfirm, onCancel, saving }: {
  projectName: string;
  departments: { DepartmentID: number; DepartmentName: string }[];
  onConfirm: (departmentID: number) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [deptID, setDeptID] = useState<number | ''>('');
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, maxWidth: 420, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text1)', marginBottom: 4 }}>Reassign Department</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)', marginBottom: 8 }}>{projectName}</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.5 }}>
          This rebuilds the approval chain against the new department's Director/EMT contacts. Only allowed while no approval step has been actioned yet.
        </div>
        <select style={fieldStyle} value={deptID} onChange={e => setDeptID(e.target.value ? parseInt(e.target.value) : '')}>
          <option value="">Select department...</option>
          {departments.map(d => <option key={d.DepartmentID} value={d.DepartmentID}>{d.DepartmentName}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="btn btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={() => deptID && onConfirm(deptID)} disabled={saving || !deptID}>
            {saving ? 'Reassigning...' : 'Reassign & Rebuild Chain'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditProjectDialog({ project, onSave, onCancel, saving }: {
  project: Project;
  onSave: (updates: Partial<Project>) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<Project>({ ...project });
  const set = (field: keyof Project, value: any) => setForm(f => ({ ...f, [field]: value }));
  const APPROVAL_STATUSES = ['Draft', 'Director Review', 'Director Denied', 'EMT Review', 'EMT Denied', 'IT Review', 'IT Denied', 'CEO Review', 'CEO Denied', 'Revision', 'Paused', 'Approved', 'Denied'];
  const PROJECT_STATUSES  = ['', 'Not Started', 'In Progress', 'On Hold', 'Completed', 'Cancelled'];
  const PRIORITIES        = ['TBD', 'Critical', 'High', 'Medium', 'Low'];
  const BUDGETED          = ['TBD', 'Yes', 'No'];

  const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, display: 'block' };
  const inputStyle: React.CSSProperties = { ...fieldStyle, width: '100%', boxSizing: 'border-box' };
  const textareaStyle: React.CSSProperties = { ...inputStyle, minHeight: 70, resize: 'vertical' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text1)', marginBottom: 16 }}>Edit Project</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Project Name</label>
            <input style={inputStyle} value={form.Title} onChange={e => set('Title', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Business Owner Name</label>
            <input style={inputStyle} value={form.BusinessOwnerName || ''} onChange={e => set('BusinessOwnerName', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Business Owner Email</label>
            <input style={inputStyle} type="email" value={form.BusinessOwnerEmail || ''} onChange={e => set('BusinessOwnerEmail', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Project Manager</label>
            <input style={inputStyle} value={form.ProjectManagerName || ''} onChange={e => set('ProjectManagerName', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Project Manager Email</label>
            <input style={inputStyle} type="email" value={form.ProjectManagerEmail || ''} onChange={e => set('ProjectManagerEmail', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Priority</label>
            <select style={inputStyle} value={form.Priority || ''} onChange={e => set('Priority', e.target.value)}>{PRIORITIES.map(s => <option key={s} value={s}>{s}</option>)}</select>
          </div>
          <div>
            <label style={labelStyle}>Budgeted</label>
            <select style={inputStyle} value={form.Budgeted || ''} onChange={e => set('Budgeted', e.target.value)}>{BUDGETED.map(s => <option key={s} value={s}>{s}</option>)}</select>
          </div>
          <div>
            <label style={labelStyle}>Budget Amount</label>
            <input style={inputStyle} type="number" value={form.AmountBudgeted ?? ''} onChange={e => set('AmountBudgeted', e.target.value ? parseFloat(e.target.value) : null)} />
          </div>
          <div>
            <label style={labelStyle}>Start Date</label>
            <input style={inputStyle} type="date" value={form.StartDate ? form.StartDate.split('T')[0] : ''} onChange={e => set('StartDate', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Target End Date</label>
            <input style={inputStyle} type="date" value={form.EndDate ? form.EndDate.split('T')[0] : ''} onChange={e => set('EndDate', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Approval Status</label>
            <select style={inputStyle} value={form.ApprovalStatus || ''} onChange={e => set('ApprovalStatus', e.target.value)}>{APPROVAL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select>
          </div>
          <div>
            <label style={labelStyle}>Project Status</label>
            <select style={inputStyle} value={form.ProjectStatus || ''} onChange={e => set('ProjectStatus', e.target.value || null)}>{PROJECT_STATUSES.map(s => <option key={s} value={s}>{s || '—'}</option>)}</select>
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>IT Notes</label>
          <textarea style={{ ...textareaStyle, minHeight: 50 }} value={form.ITNotes || ''} onChange={e => set('ITNotes', e.target.value)} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Project Description</label>
          <textarea style={textareaStyle} value={form.ProjectDescription || ''} onChange={e => set('ProjectDescription', e.target.value)} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Objectives</label>
          <textarea style={textareaStyle} value={form.Objectives || ''} onChange={e => set('Objectives', e.target.value)} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Deliverables</label>
          <textarea style={textareaStyle} value={form.Deliverables || ''} onChange={e => set('Deliverables', e.target.value)} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Organizational Benefits</label>
          <textarea style={textareaStyle} value={form.OrganizationalBenefits || ''} onChange={e => set('OrganizationalBenefits', e.target.value)} />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={() => onSave(form)} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Primary Tab Bar ───────────────────────────────────────────
function TabBar({ tabs, active, onChange }: { tabs: { id: string; label: string }[]; active: string; onChange: (id: string) => void }) {
  return (
    <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 20, overflowX: 'auto' }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} style={{ padding: '10px 16px', fontSize: 13, whiteSpace: 'nowrap', fontWeight: active === t.id ? 600 : 400, background: 'none', border: 'none', cursor: 'pointer', borderBottom: active === t.id ? '2px solid var(--accent)' : '2px solid transparent', color: active === t.id ? 'var(--accent)' : 'var(--text2)' }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Settings Sub-Tab Bar (visually lighter) ───────────────────
function SubTabBar({ tabs, active, onChange }: { tabs: { id: string; label: string }[]; active: string; onChange: (id: string) => void }) {
  return (
    <div style={{ display: 'flex', gap: 2, marginBottom: 20, background: 'var(--surface2, #f8fafc)', borderRadius: 8, padding: 4, border: '1px solid var(--border)', width: 'fit-content' }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} style={{ padding: '6px 14px', fontSize: 12, whiteSpace: 'nowrap', fontWeight: active === t.id ? 600 : 400, background: active === t.id ? 'var(--surface)' : 'none', border: active === t.id ? '1px solid var(--border)' : '1px solid transparent', borderRadius: 6, cursor: 'pointer', color: active === t.id ? 'var(--text1)' : 'var(--text2)', boxShadow: active === t.id ? '0 1px 3px rgba(0,0,0,0.06)' : 'none', transition: 'all 0.1s' }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

const fieldStyle: React.CSSProperties = {
  padding: '5px 8px', fontSize: 12,
  border: '1px solid var(--border2)', borderRadius: 6,
  background: 'var(--surface)', color: 'var(--text1)', fontFamily: 'var(--font)',
};

function ToggleSwitch({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button onClick={() => !disabled && onChange(!checked)} style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: disabled ? 'default' : 'pointer', background: checked ? 'var(--accent, #2563eb)' : 'var(--border2, #cbd5e1)', position: 'relative', transition: 'background 0.2s', flexShrink: 0, opacity: disabled ? 0.5 : 1 }} aria-label="Toggle">
      <span style={{ position: 'absolute', top: 3, left: checked ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </button>
  );
}

function SectionCard({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, marginBottom: 20, overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', background: 'var(--surface2, #f8fafc)' }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text1)' }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{description}</div>
      </div>
      <div style={{ padding: '20px' }}>{children}</div>
    </div>
  );
}

const APP_ROLES = ['', 'IT.Admin', 'IT.Approver', 'Director', 'EMT', 'Staff'];
const APP_ROLE_LABELS: Record<string, string> = {
  '':           'Staff (default)',
  'IT.Admin':   'IT Admin',
  'IT.Approver':'IT Approver',
  'Director':   'Director',
  'EMT':        'Executive Management Team',
  'Staff':      'Staff',
};

// ── Stats Header ──────────────────────────────────────────────
function StatsHeader() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  useEffect(() => { api.admin.stats.get().then(setStats).catch(() => {}); }, []);
  if (!stats) return null;
  const items = [
    { label: 'Users',             value: stats.TotalUsers       },
    { label: 'Active Projects',   value: stats.ActiveProjects   },
    { label: 'Pending Approvals', value: stats.PendingApprovals },
    { label: 'Drafts',            value: stats.DraftProjects    },
    { label: 'Need Revision',     value: stats.RevisionProjects },
  ];
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
      {items.map(item => (
        <div key={item.label} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 16px', minWidth: 100 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text1)' }}>{item.value}</div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>{item.label}</div>
        </div>
      ))}
      {stats.LastAuditEvent && (
        <div style={{ display: 'flex', alignItems: 'flex-end', fontSize: 11, color: 'var(--text3)', paddingBottom: 4 }}>
          Last activity: {formatDateTime(stats.LastAuditEvent)}
        </div>
      )}
    </div>
  );
}

// ── Departments Tab ───────────────────────────────────────────
function DepartmentsTab({ showToast, canEdit }: { showToast: (m: string, t: 'success' | 'error') => void; canEdit: boolean }) {
  const [depts, setDepts]       = useState<Department[]>([]);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<Department>>({});
  const [saving, setSaving]     = useState(false);
  const [showNew, setShowNew]   = useState(false);
  const [newForm, setNewForm]   = useState({ DepartmentName: '', DirectorName: '', DirectorEmail: '', AGMName: '', AGMEmail: '' });
  const [confirm, setConfirm]   = useState<{ id: number; name: string } | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.admin.departments.list().then(setDepts).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  function startEdit(d: Department) { setEditing(d.DepartmentID); setEditForm({ ...d }); }
  function cancelEdit() { setEditing(null); setEditForm({}); }

  async function saveEdit() {
    if (!editing) return;
    setSaving(true);
    try { await api.admin.departments.update(editing, editForm); showToast('Department updated', 'success'); setEditing(null); load(); }
    catch (e: any) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  }

  async function saveNew() {
    if (!newForm.DepartmentName.trim()) { showToast('Department name is required', 'error'); return; }
    setSaving(true);
    try { await api.admin.departments.create(newForm); showToast('Department created', 'success'); setShowNew(false); setNewForm({ DepartmentName: '', DirectorName: '', DirectorEmail: '', AGMName: '', AGMEmail: '' }); load(); }
    catch (e: any) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  }

  async function deleteDept(id: number) {
    try { await api.admin.departments.delete(id); showToast('Department deleted', 'success'); setConfirm(null); load(); }
    catch (e: any) { showToast(e.message, 'error'); }
  }

  async function toggleActive(d: Department) {
    try { await api.admin.departments.update(d.DepartmentID, { ...d, IsActive: !d.IsActive }); showToast(`Department ${d.IsActive ? 'deactivated' : 'activated'}`, 'success'); load(); }
    catch (e: any) { showToast(e.message, 'error'); }
  }

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>Loading departments...</div>;

  return (
    <div>
      {confirm && <ConfirmDialog title="Delete Department" itemName={confirm.name} message="This will deactivate the department. Projects in this department will not be affected." onConfirm={() => deleteDept(confirm.id)} onCancel={() => setConfirm(null)} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: 'var(--text2)' }}>{depts.length} departments</span>
        <button className="btn btn-primary btn-sm" onClick={() => setShowNew(s => !s)} disabled={!canEdit}>+ Add Department</button>
      </div>
      {showNew && (
        <div className="card" style={{ padding: 16, marginBottom: 16, background: 'var(--surface2, #f8fafc)' }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12 }}>New Department</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            {(['DepartmentName', 'DirectorName', 'DirectorEmail', 'AGMName', 'AGMEmail'] as const).map(k => (
              <input key={k} style={fieldStyle}
                placeholder={k === 'AGMName' ? 'EMT Name' : k === 'AGMEmail' ? 'EMT Email' : k.replace(/([A-Z])/g, ' $1').trim()}
                value={(newForm as any)[k]}
                onChange={e => setNewForm(f => ({ ...f, [k]: e.target.value }))}
              />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={saveNew} disabled={saving || !canEdit}>Save</button>
            <button className="btn btn-sm" onClick={() => setShowNew(false)}>Cancel</button>
          </div>
        </div>
      )}
      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="data-table">
          <thead><tr><th>Department</th><th>Director</th><th>EMT Contact</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {depts.map(d => editing === d.DepartmentID ? (
              <tr key={d.DepartmentID} style={{ background: 'var(--surface2, #f8fafc)' }}>
                <td><input style={{ ...fieldStyle, width: '100%' }} value={editForm.DepartmentName || ''} onChange={e => setEditForm(f => ({ ...f, DepartmentName: e.target.value }))} /></td>
                <td>
                  <input style={{ ...fieldStyle, width: '100%', marginBottom: 4 }} placeholder="Director name" value={editForm.DirectorName || ''} onChange={e => setEditForm(f => ({ ...f, DirectorName: e.target.value }))} />
                  <input style={{ ...fieldStyle, width: '100%' }} placeholder="Director email" value={editForm.DirectorEmail || ''} onChange={e => setEditForm(f => ({ ...f, DirectorEmail: e.target.value }))} />
                </td>
                <td>
                  <input style={{ ...fieldStyle, width: '100%', marginBottom: 4 }} placeholder="EMT name" value={editForm.AGMName || ''} onChange={e => setEditForm(f => ({ ...f, AGMName: e.target.value }))} />
                  <input style={{ ...fieldStyle, width: '100%' }} placeholder="EMT email" value={editForm.AGMEmail || ''} onChange={e => setEditForm(f => ({ ...f, AGMEmail: e.target.value }))} />
                </td>
                <td />
                <td><div style={{ display: 'flex', gap: 6 }}><button className="btn btn-primary btn-sm" onClick={saveEdit} disabled={saving || !canEdit}>Save</button><button className="btn btn-sm" onClick={cancelEdit}>Cancel</button></div></td>
              </tr>
            ) : (
              <tr key={d.DepartmentID}>
                <td style={{ fontWeight: 500, fontSize: 13 }}>{d.DepartmentName}</td>
                <td><div style={{ fontSize: 13 }}>{d.DirectorName || '—'}</div><div style={{ fontSize: 11, color: 'var(--text2)' }}>{d.DirectorEmail || ''}</div></td>
                <td><div style={{ fontSize: 13 }}>{d.AGMName || '—'}</div><div style={{ fontSize: 11, color: 'var(--text2)' }}>{d.AGMEmail || ''}</div></td>
                <td><span className={`badge ${d.IsActive ? 'badge-success' : 'badge-neutral'}`}>{d.IsActive ? 'Active' : 'Inactive'}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm" onClick={() => startEdit(d)} disabled={!canEdit}>Edit</button>
                    <button className="btn btn-sm" onClick={() => toggleActive(d)} disabled={!canEdit} style={{ color: d.IsActive ? 'var(--danger-text, #991b1b)' : 'var(--success-text, #065f46)' }}>{d.IsActive ? 'Deactivate' : 'Activate'}</button>
                    <button className="btn btn-sm" onClick={() => setConfirm({ id: d.DepartmentID, name: d.DepartmentName })} disabled={!canEdit} style={{ color: 'var(--danger-text, #991b1b)' }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Users Tab — full CRUD ─────────────────────────────────────
function UsersTab({ showToast, canEdit }: { showToast: (m: string, t: 'success' | 'error') => void; canEdit: boolean }) {
  const [users, setUsers]           = useState<User[]>([]);
  const [loading, setLoading]       = useState(true);
  const [editing, setEditing]       = useState<number | null>(null);
  const [editForm, setEditForm]     = useState<Partial<User & { DisplayName: string; Email: string }>>({});
  const [saving, setSaving]         = useState(false);
  const [search, setSearch]         = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [confirm, setConfirm]       = useState<{ id: number; name: string } | null>(null);
  const [selected, setSelected]     = useState<Set<number>>(new Set());
  const [bulkRole, setBulkRole]     = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [showNew, setShowNew]       = useState(false);
  const [newForm, setNewForm]       = useState({ DisplayName: '', Email: '', AppRole: '' });
  const [newSaving, setNewSaving]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.admin.users.list().then(setUsers).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  function startEdit(u: User) {
    setEditing(u.UserID);
    setEditForm({ ...u, DisplayName: u.DisplayName, Email: u.Email });
  }
  function cancelEdit() { setEditing(null); setEditForm({}); }

  async function saveEdit() {
    if (!editing) return;
    setSaving(true);
    try {
      await api.admin.users.update(editing, {
        DisplayName:  editForm.DisplayName  || undefined,
        Email:        editForm.Email        || undefined,
        AppRole:      editForm.AppRole      || null,
        IsActive:     editForm.IsActive,
      });
      showToast('User updated', 'success');
      setEditing(null); load();
    } catch (e: any) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  }

  async function saveNew() {
    if (!newForm.DisplayName.trim()) { showToast('Name is required', 'error'); return; }
    if (!newForm.Email.trim())       { showToast('Email is required', 'error'); return; }
    setNewSaving(true);
    try {
      await api.admin.users.create(newForm);
      showToast('User created', 'success');
      setShowNew(false);
      setNewForm({ DisplayName: '', Email: '', AppRole: '' });
      load();
    } catch (e: any) { showToast(e.message, 'error'); }
    finally { setNewSaving(false); }
  }

  async function deleteUser(id: number) {
    try { await api.admin.users.delete(id); showToast('User deleted', 'success'); setConfirm(null); load(); }
    catch (e: any) { showToast(e.message, 'error'); }
  }

  async function applyBulkRole() {
    if (!bulkRole || selected.size === 0) return;
    setBulkSaving(true);
    try {
      const updates = Array.from(selected).map(userID => ({ userID, appRole: bulkRole }));
      await api.admin.users.bulkUpdate(updates);
      showToast(`${updates.length} users updated to ${APP_ROLE_LABELS[bulkRole] || bulkRole}`, 'success');
      setSelected(new Set()); setBulkRole(''); load();
    } catch (e: any) { showToast(e.message, 'error'); }
    finally { setBulkSaving(false); }
  }

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchesSearch = !search || [u.DisplayName, u.Email, u.AppRole || ''].some(s => s.toLowerCase().includes(q));
    const matchesRole   = !roleFilter || u.AppRole === roleFilter || (roleFilter === 'Staff' && !u.AppRole);
    return matchesSearch && matchesRole;
  });

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>Loading users...</div>;

  return (
    <div>
      {confirm && <ConfirmDialog title="Delete User" itemName={confirm.name} message="They will be deactivated and can no longer access the portal." onConfirm={() => deleteUser(confirm.id)} onCancel={() => setConfirm(null)} />}

      {/* Add User panel */}
      {showNew && (
        <div className="card" style={{ padding: 16, marginBottom: 16, background: 'var(--surface2, #f8fafc)' }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12 }}>Add User</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>Full Name <span style={{ color: 'var(--danger-text, #991b1b)' }}>*</span></div>
              <input style={{ ...fieldStyle, width: '100%', padding: '7px 10px' }} placeholder="e.g. John Smith" value={newForm.DisplayName} onChange={e => setNewForm(f => ({ ...f, DisplayName: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>Email Address <span style={{ color: 'var(--danger-text, #991b1b)' }}>*</span></div>
              <input style={{ ...fieldStyle, width: '100%', padding: '7px 10px' }} placeholder="e.g. jsmith@thewoodlands-tx.gov" value={newForm.Email} onChange={e => setNewForm(f => ({ ...f, Email: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>Role <span style={{ color: 'var(--text3)' }}>(optional)</span></div>
              <select style={{ ...fieldStyle, width: '100%', padding: '7px 10px' }} value={newForm.AppRole} onChange={e => setNewForm(f => ({ ...f, AppRole: e.target.value }))}>
                {APP_ROLES.map(r => <option key={r} value={r}>{APP_ROLE_LABELS[r] || r || 'Staff (default)'}</option>)}
              </select>
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12 }}>
            A placeholder account will be created. The user's real Entra ID will be linked automatically the first time they log in.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={saveNew} disabled={newSaving || !canEdit}>{newSaving ? 'Creating...' : 'Create User'}</button>
            <button className="btn btn-sm" onClick={() => { setShowNew(false); setNewForm({ DisplayName: '', Email: '', AppRole: '' }); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="field-input" style={{ width: 240, fontSize: 13 }} placeholder="Search name or email..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...fieldStyle, padding: '7px 10px', fontSize: 13 }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="">All roles</option>
          {APP_ROLES.filter(r => r).map(r => <option key={r} value={r}>{APP_ROLE_LABELS[r] || r}</option>)}
          <option value="Staff">Staff (no role)</option>
        </select>
        <span style={{ fontSize: 13, color: 'var(--text2)', marginLeft: 'auto' }}>{filtered.length} users</span>
        <button className="btn btn-primary btn-sm" onClick={() => setShowNew(s => !s)} disabled={!canEdit}>+ Add User</button>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--accent-bg, #eff6ff)', borderRadius: 8, marginBottom: 12, border: '1px solid var(--accent)' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>{selected.size} selected</span>
          <select style={{ ...fieldStyle, fontSize: 12 }} value={bulkRole} onChange={e => setBulkRole(e.target.value)} disabled={!canEdit}>
            <option value="">Set role...</option>
            {APP_ROLES.map(r => <option key={r} value={r}>{APP_ROLE_LABELS[r] || r}</option>)}
          </select>
          <button className="btn btn-primary btn-sm" onClick={applyBulkRole} disabled={!bulkRole || bulkSaving || !canEdit}>{bulkSaving ? 'Applying...' : 'Apply'}</button>
          <button className="btn btn-sm" onClick={() => setSelected(new Set())}>Clear selection</button>
        </div>
      )}

      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={e => setSelected(e.target.checked ? new Set(filtered.map(u => u.UserID)) : new Set())} />
              </th>
              <th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Last Login</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => editing === u.UserID ? (
              <tr key={u.UserID} style={{ background: 'var(--surface2, #f8fafc)' }}>
                <td />
                <td>
                  <input style={{ ...fieldStyle, width: '100%' }} placeholder="Full name" value={editForm.DisplayName || ''} onChange={e => setEditForm(f => ({ ...f, DisplayName: e.target.value }))} />
                </td>
                <td>
                  <input style={{ ...fieldStyle, width: '100%' }} placeholder="Email address" value={editForm.Email || ''} onChange={e => setEditForm(f => ({ ...f, Email: e.target.value }))} />
                </td>
                <td>
                  <select style={fieldStyle} value={editForm.AppRole || ''} onChange={e => setEditForm(f => ({ ...f, AppRole: e.target.value || null }))}>
                    {APP_ROLES.map(r => <option key={r} value={r}>{APP_ROLE_LABELS[r] || r}</option>)}
                  </select>
                </td>
                <td>
                  <select style={fieldStyle} value={editForm.IsActive ? '1' : '0'} onChange={e => setEditForm(f => ({ ...f, IsActive: e.target.value === '1' }))}>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </td>
                <td style={{ fontSize: 12, color: 'var(--text2)' }}>{formatDateTime(u.LastLoginAt)}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-primary btn-sm" onClick={saveEdit} disabled={saving || !canEdit}>Save</button>
                    <button className="btn btn-sm" onClick={cancelEdit}>Cancel</button>
                  </div>
                </td>
              </tr>
            ) : (
              <tr key={u.UserID}>
                <td><input type="checkbox" checked={selected.has(u.UserID)} disabled={!canEdit} onChange={e => setSelected(prev => { const next = new Set(prev); e.target.checked ? next.add(u.UserID) : next.delete(u.UserID); return next; })} /></td>
                <td style={{ fontWeight: 500, fontSize: 13 }}>{u.DisplayName}</td>
                <td style={{ fontSize: 12, color: 'var(--text2)' }}>{u.Email}</td>
                <td>{u.AppRole ? <span className="badge badge-info">{APP_ROLE_LABELS[u.AppRole] || u.AppRole}</span> : <span style={{ fontSize: 12, color: 'var(--text2)' }}>Staff</span>}</td>
                <td><span className={`badge ${u.IsActive ? 'badge-success' : 'badge-neutral'}`}>{u.IsActive ? 'Active' : 'Inactive'}</span></td>
                <td style={{ fontSize: 12, color: 'var(--text2)' }}>{formatDateTime(u.LastLoginAt)}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm" onClick={() => startEdit(u)} disabled={!canEdit}>Edit</button>
                    <button className="btn btn-sm" onClick={() => setConfirm({ id: u.UserID, name: u.DisplayName })} disabled={!canEdit} style={{ color: 'var(--danger-text, #991b1b)' }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Projects Tab ──────────────────────────────────────────────
function ProjectsTab({ showToast, canEdit }: { showToast: (m: string, t: 'success' | 'error') => void; canEdit: boolean }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading]   = useState(true);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [saving, setSaving]     = useState(false);
  const [search, setSearch]     = useState('');
  const [confirm, setConfirm]   = useState<{ id: number; name: string } | null>(null);
  const [departments, setDepartments] = useState<{ DepartmentID: number; DepartmentName: string }[]>([]);
  const [reassign, setReassign]       = useState<{ id: number; name: string } | null>(null);
  const [reassigning, setReassigning] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.projects.list().then(data => setProjects(data as any)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.departments.list().then(setDepartments).catch(() => {}); }, []);

  async function doReassign(departmentID: number) {
    if (!reassign) return;
    setReassigning(true);
    try {
      await api.admin.projects.reassignDepartment(reassign.id, departmentID);
      showToast('Department reassigned — approval chain rebuilt', 'success');
      setReassign(null); load();
    } catch (e: any) { showToast(e.message, 'error'); }
    finally { setReassigning(false); }
  }

  async function saveEdit(updates: Partial<Project>) {
    if (!editingProject) return;
    setSaving(true);
    try {
      await api.admin.projects.update(editingProject.ProjectID, updates);
      showToast('Project updated', 'success');
      setEditingProject(null); load();
    } catch (e: any) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  }

  async function deleteProject(id: number) {
    try { await api.admin.projects.delete(id); showToast('Project permanently deleted', 'success'); setConfirm(null); load(); }
    catch (e: any) { showToast(e.message, 'error'); }
  }

  const filtered = projects.filter(p => !search || [p.Title, p.DepartmentName, p.RequestorName].some(s => s?.toLowerCase().includes(search.toLowerCase())));

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>Loading projects...</div>;

  return (
    <div>
      {confirm && <ConfirmDialog title="Permanently Delete Project" itemName={confirm.name} message="This permanently deletes the project, its approvals, comments, attachments (including the uploaded files), and audit history. There is no undo." requireTypedConfirmation onConfirm={() => deleteProject(confirm.id)} onCancel={() => setConfirm(null)} />}
      {reassign && <ReassignDepartmentDialog projectName={reassign.name} departments={departments} saving={reassigning} onConfirm={doReassign} onCancel={() => setReassign(null)} />}
      {editingProject && <EditProjectDialog project={editingProject} saving={saving} onSave={saveEdit} onCancel={() => setEditingProject(null)} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <input className="field-input" style={{ width: 280, fontSize: 13 }} placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)} />
        <span style={{ fontSize: 13, color: 'var(--text2)' }}>{filtered.length} projects</span>
      </div>
      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="data-table">
          <thead><tr><th>Project</th><th>Department</th><th>Priority</th><th>Approval Status</th><th>Project Status</th><th>IT Notes</th><th>Actions</th></tr></thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.ProjectID}>
                <td><div style={{ fontWeight: 500, fontSize: 13 }}>{p.Title}</div><div style={{ fontSize: 11, color: 'var(--text2)' }}>{p.RequestorName}</div></td>
                <td style={{ fontSize: 12, color: 'var(--text2)' }}>{p.DepartmentName}</td>
                <td><span className={`badge badge-${p.Priority === 'High' || p.Priority === 'Critical' ? 'danger' : p.Priority === 'Low' ? 'neutral' : 'info'}`}>{p.Priority}</span></td>
                <td style={{ fontSize: 12 }}>{p.ApprovalStatus}</td>
                <td style={{ fontSize: 12, color: 'var(--text2)' }}>{p.ProjectStatus || '—'}</td>
                <td style={{ fontSize: 12, color: 'var(--text2)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.ITNotes || '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button className="btn btn-sm" onClick={() => navigate(`/projects/${p.ProjectID}`)}>View</button>
                    <button className="btn btn-sm" onClick={() => setEditingProject(p)} disabled={!canEdit}>Edit</button>
                    <button className="btn btn-sm" onClick={() => setReassign({ id: p.ProjectID, name: p.Title })} disabled={!canEdit}>Reassign Dept</button>
                    <button className="btn btn-sm" onClick={() => setConfirm({ id: p.ProjectID, name: p.Title })} disabled={!canEdit} style={{ color: 'var(--danger-text, #991b1b)' }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Form Settings Sub-Tab ─────────────────────────────────────
function FormSettingsTab({ showToast, canEdit }: { showToast: (m: string, t: 'success' | 'error') => void; canEdit: boolean }) {
  const [fields, setFields]   = useState<FormFieldSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  useEffect(() => { api.formSettings.get().then(setFields).finally(() => setLoading(false)); }, []);
  async function save() { setSaving(true); try { await api.formSettings.update(fields); showToast('Form settings saved', 'success'); } catch (e: any) { showToast(e.message, 'error'); } finally { setSaving(false); } }
  function toggle(key: string, prop: 'IsVisible' | 'IsRequired' | 'IsEnabled', val: boolean) { setFields(prev => prev.map(f => f.FieldKey === key ? { ...f, [prop]: val } : f)); }
  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>Loading...</div>;
  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>Control which fields appear in the New Project Request form.</div>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !canEdit}>{saving ? 'Saving...' : 'Save Changes'}</button>
      </div>
      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="data-table">
          <thead><tr><th>Field</th><th style={{ textAlign: 'center' }}>Visible</th><th style={{ textAlign: 'center' }}>Required</th><th style={{ textAlign: 'center' }}>Enabled</th><th>Help Text</th></tr></thead>
          <tbody>
            {[...fields].sort((a, b) => a.SortOrder - b.SortOrder).map(f => (
              <tr key={f.FieldKey}>
                <td><div style={{ fontWeight: 500, fontSize: 13 }}>{f.Label}</div><div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace' }}>{f.FieldKey}</div></td>
                <td style={{ textAlign: 'center' }}><ToggleSwitch checked={f.IsVisible} onChange={v => toggle(f.FieldKey, 'IsVisible', v)} disabled={!canEdit} /></td>
                <td style={{ textAlign: 'center' }}><ToggleSwitch checked={f.IsRequired} onChange={v => toggle(f.FieldKey, 'IsRequired', v)} disabled={!canEdit || !f.IsVisible} /></td>
                <td style={{ textAlign: 'center' }}><ToggleSwitch checked={f.IsEnabled} onChange={v => toggle(f.FieldKey, 'IsEnabled', v)} disabled={!canEdit || !f.IsVisible} /></td>
                <td style={{ fontSize: 12, color: 'var(--text2)', maxWidth: 280 }}>{f.HelpText || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Portal Settings Sub-Tab ───────────────────────────────────
function PortalSettingsTab({ showToast, canEdit }: { showToast: (m: string, t: 'success' | 'error') => void; canEdit: boolean }) {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  useEffect(() => { api.admin.portalSettings.get().then(s => setSettings(s as any)).finally(() => setLoading(false)); }, []);
  async function save(key: string, value: string) { setSaving(true); try { await api.admin.portalSettings.update({ [key]: value } as any); setSettings(prev => ({ ...prev, [key]: value })); showToast('Setting saved', 'success'); } catch (e: any) { showToast(e.message, 'error'); } finally { setSaving(false); } }
  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>Loading...</div>;
  // Note: email sending is controlled from Settings -> System ("Send email
  // notifications"), not here. There used to be a second "Email notifications
  // enabled" toggle on this tab that wrote to a setting nothing ever read --
  // removed to avoid the two disagreeing.
  const portalToggles = [
    { key: 'RequirementsTrackerVisible', label: 'Requirements Tracker visible to users', desc: 'When off, only IT Admins see the Requirements Tracker nav item and the Requirements tab on Project Detail pages.' },
    { key: 'MaintenanceModeEnabled',     label: 'Maintenance mode',                      desc: 'When on, everyone sees a maintenance banner and non-admins cannot submit new requests (drafts can still be saved).' },
    { key: 'UATTestingVisible',          label: 'UAT Testing visible in navigation',     desc: 'When on, the UAT Testing link appears in the sidebar for all logged-in users.' },
  ];
  return (
    <div>
      {portalToggles.map(t => (
        <SectionCard key={t.key} title={t.label} description={t.desc}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ToggleSwitch checked={settings[t.key] === '1'} onChange={v => save(t.key, v ? '1' : '0')} disabled={saving || !canEdit} />
            <span style={{ fontSize: 13, fontWeight: 500, color: settings[t.key] === '1' ? 'var(--success-text, #065f46)' : 'var(--text3)' }}>{settings[t.key] === '1' ? 'Enabled' : 'Disabled'}</span>
          </div>
        </SectionCard>
      ))}
      <SectionCard title="Masquerade Session Timeout" description="How long an admin View As session lasts before automatically ending.">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input type="number" style={{ ...fieldStyle, width: 80, padding: '7px 10px', fontSize: 13 }} value={settings['MasqueradeTimeoutMinutes'] || '30'} onChange={e => setSettings(prev => ({ ...prev, MasqueradeTimeoutMinutes: e.target.value }))} min="5" max="120" disabled={!canEdit} />
          <span style={{ fontSize: 13, color: 'var(--text2)' }}>minutes</span>
          <button className="btn btn-sm" onClick={() => save('MasqueradeTimeoutMinutes', settings['MasqueradeTimeoutMinutes'] || '30')} disabled={saving || !canEdit}>Save</button>
        </div>
      </SectionCard>
      <SectionCard title="Attachment Settings" description="Control file upload limits for project attachments.">
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>Max file size (MB)</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="number" style={{ ...fieldStyle, width: 80, padding: '7px 10px', fontSize: 13 }} value={settings['MaxAttachmentSizeMB'] || '25'} onChange={e => setSettings(prev => ({ ...prev, MaxAttachmentSizeMB: e.target.value }))} min="1" max="100" disabled={!canEdit} />
              <button className="btn btn-sm" onClick={() => save('MaxAttachmentSizeMB', settings['MaxAttachmentSizeMB'] || '25')} disabled={saving || !canEdit}>Save</button>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>Allowed file types</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', background: 'var(--surface2)', borderRadius: 6, padding: '7px 12px', fontFamily: 'monospace' }}>{settings['AllowedAttachmentTypes'] || '.pdf,.docx,.xlsx,.png,.jpg'}</div>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}

// ── Workflow Sub-Tab ──────────────────────────────────────────
function WorkflowTab({ showToast, canEdit }: { showToast: (m: string, t: 'success' | 'error') => void; canEdit: boolean }) {
  const { user, dbUserID }    = useAuth();
  const [steps, setSteps]     = useState<ChainStep[]>([]);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [ceoEditing, setCeoEditing]     = useState(false);
  const [spendEditing, setSpendEditing] = useState(false);
  const [ceoForm, setCeoForm]   = useState({ ceoName: '', ceoEmail: '' });
  const [spendForm, setSpendForm] = useState<SpendingAuthority>({ manager: 0, assistantDirector: 0, director: 0, agm: 0, ceo: 0 });

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.admin.chain.list(), api.admin.settings.get()])
      .then(([chainRows, settingsData]) => {
        setSteps((chainRows as any[]).map(r => ({ ...r, IsEnabled: !!r.IsEnabled })));
        const s = settingsData as AppSettings;
        setSettings(s);
        setCeoForm({ ceoName: s.ceoName || '', ceoEmail: s.ceoEmail || '' });
        setSpendForm(s.spendingAuthority || { manager: 5000, assistantDirector: 10000, director: 15000, agm: 20000, ceo: 50000 });
      })
      .catch(() => showToast('Failed to load workflow settings', 'error'))
      .finally(() => setLoading(false));
  }, [showToast]);
  useEffect(() => { load(); }, [load]);

  async function saveSettings(updated: Partial<AppSettings>) { setSaving(true); try { await api.admin.settings.update(updated); setSettings(s => s ? { ...s, ...updated } : s); showToast('Settings saved', 'success'); } catch (e: any) { showToast(e.message, 'error'); } finally { setSaving(false); } }

  async function toggleStep(step: ChainStep) {
    setSaving(true);
    try { await api.admin.chain.update(step.StepID, { isEnabled: !step.IsEnabled, stepOrder: step.StepOrder, actorEmail: user?.email || '', actorName: user?.name || '', actorUserID: dbUserID || null }); setSteps(prev => prev.map(s => s.StepID === step.StepID ? { ...s, IsEnabled: !s.IsEnabled } : s)); showToast(`${step.StepName} step ${step.IsEnabled ? 'disabled' : 'enabled'}`, 'success'); }
    catch (e: any) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  }

  async function moveStep(stepID: number, direction: 'up' | 'down') {
    const sorted  = [...steps].sort((a, b) => a.StepOrder - b.StepOrder);
    const idx     = sorted.findIndex(s => s.StepID === stepID);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const current = sorted[idx]; const swap = sorted[swapIdx];
    setSaving(true);
    try {
      await Promise.all([
        api.admin.chain.update(current.StepID, { isEnabled: current.IsEnabled, stepOrder: swap.StepOrder,    actorEmail: user?.email || '', actorName: user?.name || '', actorUserID: dbUserID || null }),
        api.admin.chain.update(swap.StepID,    { isEnabled: swap.IsEnabled,    stepOrder: current.StepOrder, actorEmail: user?.email || '', actorName: user?.name || '', actorUserID: dbUserID || null }),
      ]);
      setSteps(prev => prev.map(s => { if (s.StepID === current.StepID) return { ...s, StepOrder: swap.StepOrder }; if (s.StepID === swap.StepID) return { ...s, StepOrder: current.StepOrder }; return s; }));
      showToast('Order updated', 'success');
    } catch (e: any) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  }

  const SPEND_LABELS: { key: keyof SpendingAuthority; label: string }[] = [
    { key: 'manager',           label: 'Manager' },
    { key: 'assistantDirector', label: 'Assistant Director' },
    { key: 'director',          label: 'Director' },
    { key: 'agm',               label: 'EMT / C-Suite' },
    { key: 'ceo',               label: 'CEO' },
  ];

  const sortedSteps = [...steps].sort((a, b) => a.StepOrder - b.StepOrder);
  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>Loading...</div>;
  if (!settings) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>Failed to load settings.</div>;

  return (
    <div>
      <SectionCard title="Approval Chain" description="Enable or disable steps and reorder them. Changes take effect on all future submissions.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          {sortedSteps.map((step, idx) => (
            <div key={step.StepID} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderRadius: 8, background: step.IsEnabled ? 'var(--surface2, #f8fafc)' : 'var(--surface)', border: `1px solid ${step.IsEnabled ? 'var(--border)' : 'var(--border2)'}`, opacity: step.IsEnabled ? 1 : 0.6, transition: 'all 0.15s' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, background: step.IsEnabled ? 'var(--accent)' : 'var(--border2)', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{step.StepOrder}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)' }}>{step.StepName === 'AGM' ? 'EMT (Executive Management Team)' : step.StepName}</div>
                  <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 1 }}>{step.IsEnabled ? 'Active in chain' : 'Disabled — skipped for all projects'}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <button onClick={() => moveStep(step.StepID, 'up')} disabled={saving || idx === 0 || !canEdit} style={{ background: 'none', border: '1px solid var(--border2)', borderRadius: 4, width: 22, height: 22, cursor: idx === 0 ? 'default' : 'pointer', fontSize: 10, opacity: idx === 0 ? 0.3 : 1, color: 'var(--text2)' }}>▲</button>
                  <button onClick={() => moveStep(step.StepID, 'down')} disabled={saving || idx === sortedSteps.length - 1 || !canEdit} style={{ background: 'none', border: '1px solid var(--border2)', borderRadius: 4, width: 22, height: 22, cursor: idx === sortedSteps.length - 1 ? 'default' : 'pointer', fontSize: 10, opacity: idx === sortedSteps.length - 1 ? 0.3 : 1, color: 'var(--text2)' }}>▼</button>
                </div>
                <ToggleSwitch checked={step.IsEnabled} onChange={() => toggleStep(step)} disabled={saving || !canEdit} />
              </div>
            </div>
          ))}
        </div>
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Live Preview</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {sortedSteps.filter(s => s.IsEnabled).map((step, idx, arr) => (
              <React.Fragment key={step.StepID}>
                <div style={{ padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: 'var(--accent-bg, #eff6ff)', color: 'var(--accent)', border: '1px solid var(--accent)' }}>{step.StepName === 'AGM' ? 'EMT' : step.StepName}</div>
                {idx < arr.length - 1 && <span style={{ color: 'var(--text3)', fontSize: 14 }}>→</span>}
              </React.Fragment>
            ))}
            {sortedSteps.filter(s => s.IsEnabled).length === 0 && <span style={{ fontSize: 12, color: 'var(--danger-text, #991b1b)' }}>⚠ No steps enabled — all projects will auto-approve</span>}
          </div>
        </div>
      </SectionCard>

      <SectionCard title="CEO / President" description="Used when the CEO step is enabled in the approval chain.">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--text2)' }}>Used for CEO approval step notifications and display.</div>
          {!ceoEditing && <button className="btn btn-sm" onClick={() => setCeoEditing(true)} disabled={!canEdit}>Edit</button>}
        </div>
        {ceoEditing ? (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div><div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>Full Name</div><input style={{ ...fieldStyle, width: '100%', padding: '8px 10px', fontSize: 13 }} value={ceoForm.ceoName} onChange={e => setCeoForm(f => ({ ...f, ceoName: e.target.value }))} placeholder="e.g. Monique Sharp" /></div>
              <div><div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>Email Address</div><input style={{ ...fieldStyle, width: '100%', padding: '8px 10px', fontSize: 13 }} value={ceoForm.ceoEmail} onChange={e => setCeoForm(f => ({ ...f, ceoEmail: e.target.value }))} placeholder="e.g. MSharp@thewoodlands-tx.gov" /></div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={async () => { if (!ceoForm.ceoName.trim() || !ceoForm.ceoEmail.trim()) { showToast('CEO name and email are required', 'error'); return; } await saveSettings({ ceoName: ceoForm.ceoName, ceoEmail: ceoForm.ceoEmail }); setCeoEditing(false); }} disabled={saving || !canEdit}>Save</button>
              <button className="btn btn-sm" onClick={() => { setCeoEditing(false); setCeoForm({ ceoName: settings.ceoName, ceoEmail: settings.ceoEmail }); }}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, background: 'var(--surface2, #f8fafc)', border: '1px solid var(--border)' }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--accent-bg, #eff6ff)', border: '2px solid var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'var(--accent)', flexShrink: 0 }}>{settings.ceoName ? settings.ceoName[0].toUpperCase() : '?'}</div>
            <div><div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)' }}>{settings.ceoName || '—'}</div><div style={{ fontSize: 12, color: 'var(--text2)' }}>{settings.ceoEmail || '—'}</div></div>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Spending Authority Thresholds" description="When enabled, projects are flagged if their estimated budget exceeds role thresholds.">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
          <div><div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text1)' }}>Enable spending authority</div><div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>Flags projects that exceed role thresholds.</div></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: settings.spendingAuthorityEnabled ? 'var(--success-text, #065f46)' : 'var(--text3)' }}>{settings.spendingAuthorityEnabled ? 'Enabled' : 'Disabled'}</span>
            <ToggleSwitch checked={!!settings.spendingAuthorityEnabled} onChange={v => saveSettings({ spendingAuthorityEnabled: v })} disabled={saving || !canEdit} />
          </div>
        </div>
        <div style={{ opacity: settings.spendingAuthorityEnabled ? 1 : 0.4, pointerEvents: settings.spendingAuthorityEnabled ? 'auto' : 'none', transition: 'opacity 0.2s' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--text2)' }}>Thresholds indicate approval authority on project requests.</div>
            {!spendEditing && <button className="btn btn-sm" onClick={() => setSpendEditing(true)} disabled={!canEdit}>Edit</button>}
          </div>
          {spendEditing ? (
            <div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                {SPEND_LABELS.map(({ key, label }) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text1)', minWidth: 160 }}>{label}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ fontSize: 13, color: 'var(--text2)' }}>$</span><input type="number" style={{ ...fieldStyle, width: 120, padding: '6px 10px', fontSize: 13 }} value={spendForm[key]} onChange={e => setSpendForm(f => ({ ...f, [key]: parseInt(e.target.value) || 0 }))} /></div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={async () => { await saveSettings({ spendingAuthority: spendForm }); setSpendEditing(false); }} disabled={saving || !canEdit}>Save</button>
                <button className="btn btn-sm" onClick={() => { setSpendEditing(false); setSpendForm(settings.spendingAuthority); }}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {SPEND_LABELS.map(({ key, label }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderRadius: 8, background: 'var(--surface2, #f8fafc)', border: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 13, color: 'var(--text1)' }}>{label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>{formatCurrency(spendForm[key])}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}

// ── System Sub-Tab ────────────────────────────────────────────
function SystemTab({ showToast, canEdit }: { showToast: (m: string, t: 'success' | 'error') => void; canEdit: boolean }) {
  const { testModeActive, setTestModeActive } = useTestMode();
  const [settings, setSettings]         = useState<AppSettings | null>(null);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [newRecipient, setNewRecipient] = useState('');
  const [testingNotif, setTestingNotif] = useState(false);

  async function sendTestNotification() {
    setTestingNotif(true);
    try {
      const result = await api.admin.settings.sendTestNotification();
      if (result.emailResult === 'skipped-notifications-disabled') {
        showToast('In-app notification sent (check your bell icon). Email was skipped — turn on Email Notifications above to test delivery.', 'success');
      } else if (result.emailResult === 'sent-to-test-recipients') {
        showToast('Sent — check your bell icon and the Test Recipients below.', 'success');
      } else {
        showToast('Sent — check your bell icon and your own inbox.', 'success');
      }
    } catch (e: any) { showToast(e.message, 'error'); }
    finally { setTestingNotif(false); }
  }

  const load = useCallback(() => {
    setLoading(true);
    api.admin.settings.get()
      .then(data => { setSettings(data as AppSettings); setTestModeActive(!!(data as any).testModeActive); })
      .catch(() => showToast('Failed to load settings', 'error'))
      .finally(() => setLoading(false));
  }, [showToast, setTestModeActive]);
  useEffect(() => { load(); }, [load]);

  async function saveSettings(updated: Partial<AppSettings>) { setSaving(true); try { await api.admin.settings.update(updated); setSettings(s => s ? { ...s, ...updated } : s); showToast('Settings saved', 'success'); } catch (e: any) { showToast(e.message, 'error'); } finally { setSaving(false); } }

  function addRecipient() {
    if (!settings) return;
    const email = newRecipient.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { showToast('Please enter a valid email address', 'error'); return; }
    if (settings.testModeRecipients.includes(email)) { showToast('That email is already in the list', 'error'); return; }
    saveSettings({ testModeRecipients: [...settings.testModeRecipients, email] });
    setNewRecipient('');
  }

  function removeRecipient(email: string) {
    if (!settings) return;
    if (settings.testModeRecipients.length === 1) { showToast('At least one test recipient is required', 'error'); return; }
    saveSettings({ testModeRecipients: settings.testModeRecipients.filter(r => r !== email) });
  }

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>Loading...</div>;
  if (!settings) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>Failed to load settings.</div>;

  return (
    <div>
      <SectionCard title="Email Notifications" description="The single on/off switch for all outbound email — submission, approval actions, comments/questions, and department reassignment.">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div><div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text1)' }}>Send email notifications</div><div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>When enabled, emails go to directors, EMT contacts, requestors, business owners, and IT staff at each of those steps.</div></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, color: settings.emailNotificationsEnabled ? 'var(--success-text, #065f46)' : 'var(--text3)', fontWeight: 500 }}>{settings.emailNotificationsEnabled ? 'Enabled' : 'Disabled'}</span>
            <ToggleSwitch checked={settings.emailNotificationsEnabled} onChange={v => saveSettings({ emailNotificationsEnabled: v })} disabled={saving || !canEdit} />
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Test Mode Configuration" description="When Test Mode is active, all outbound emails are redirected to the recipients below instead of real approvers.">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 8, marginBottom: 20, background: testModeActive ? 'rgba(16,185,129,0.08)' : 'var(--surface2, #f8fafc)', border: `1px solid ${testModeActive ? '#6ee7b7' : 'var(--border)'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16 }}>🧪</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: testModeActive ? '#065f46' : 'var(--text1)' }}>Test Mode is currently {testModeActive ? 'ACTIVE' : 'inactive'}</div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 1 }}>{testModeActive ? 'All emails are being redirected to test recipients below.' : 'Activate to redirect all emails to test recipients.'}</div>
            </div>
          </div>
          <button onClick={() => { setTestModeActive(!testModeActive); saveSettings({ testModeActive: !testModeActive } as any); }} className="btn btn-sm" disabled={!canEdit} style={testModeActive ? { background: '#065f46', color: '#d1fae5', border: 'none' } : {}}>
            {testModeActive ? 'Exit Test Mode' : 'Activate Test Mode'}
          </button>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Test Recipients</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {settings.testModeRecipients.map(email => (
              <div key={email} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, background: 'var(--surface2, #f8fafc)', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--accent-bg, #eff6ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--accent)' }}>{email[0].toUpperCase()}</div>
                  <span style={{ fontSize: 13, color: 'var(--text1)' }}>{email}</span>
                </div>
                <button onClick={() => removeRecipient(email)} disabled={saving || !canEdit} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger-text, #991b1b)', fontSize: 12, padding: '2px 6px', borderRadius: 4 }}>Remove</button>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...fieldStyle, flex: 1, padding: '8px 10px', fontSize: 13 }} placeholder="Add email address..." value={newRecipient} onChange={e => setNewRecipient(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addRecipient(); }} disabled={!canEdit} />
            <button className="btn btn-primary btn-sm" onClick={addRecipient} disabled={saving || !newRecipient.trim() || !canEdit}>Add</button>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Test Notification Delivery" description="Send yourself a sample notification to confirm both the in-app bell and outbound email are working end-to-end, without touching real users or projects.">
        <button className="btn btn-sm" onClick={sendTestNotification} disabled={testingNotif || !canEdit}>
          {testingNotif ? 'Sending...' : 'Send Test Notification'}
        </button>
      </SectionCard>
    </div>
  );
}

// ── Settings Tab — wraps 4 sub-tabs ──────────────────────────
function SettingsTab({ showToast, canEdit }: { showToast: (m: string, t: 'success' | 'error') => void; canEdit: boolean }) {
  const [subTab, setSubTab] = useState('form-settings');
  const subTabs = [
    { id: 'form-settings',   label: 'Form Settings'   },
    { id: 'portal-settings', label: 'Portal Settings' },
    { id: 'workflow',        label: 'Workflow'         },
    { id: 'system',          label: 'System'           },
    { id: 'uat-results',     label: 'UAT Results'      },
  ];
  return (
    <div>
      <SubTabBar tabs={subTabs} active={subTab} onChange={setSubTab} />
      {subTab === 'form-settings'   && <FormSettingsTab   showToast={showToast} canEdit={canEdit} />}
      {subTab === 'portal-settings' && <PortalSettingsTab showToast={showToast} canEdit={canEdit} />}
      {subTab === 'workflow'        && <WorkflowTab       showToast={showToast} canEdit={canEdit} />}
     {subTab === 'system'          && <SystemTab         showToast={showToast} canEdit={canEdit} />}
      {subTab === 'uat-results'     && <UATResultsTab     showToast={showToast} />}
    </div>
  );
}

// ── UAT Results Sub-Tab ───────────────────────────────────────
function UATResultsTab({ showToast }: { showToast: (m: string, t: 'success' | 'error') => void }) {
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading]   = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.admin.uat.list()
      .then(res => setSessions(res.sessions))
      .catch(() => showToast('Failed to load UAT sessions', 'error'))
      .finally(() => setLoading(false));
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  async function exportCSV() {
    setExporting(true);
    try {
      const res = await api.admin.uat.export();
      if (!res.rows.length) { showToast('No UAT data to export', 'error'); return; }
      const keys = Object.keys(res.rows[0]);
      const csv = [
        keys.join(','),
        ...res.rows.map(row =>
          keys.map(k => {
            const val = row[k] === null || row[k] === undefined ? '' : String(row[k]);
            return `"${val.replace(/"/g, '""')}"`;
          }).join(',')
        )
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `uat-report-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('UAT report exported', 'success');
    } catch (e: any) {
      showToast(e.message || 'Export failed', 'error');
    } finally {
      setExporting(false);
    }
  }

  const submitted = sessions.filter(s => s.Status === 'submitted');
  const inProgress = sessions.filter(s => s.Status === 'in_progress');

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>Loading UAT sessions...</div>;

  return (
    <div>
      {/* Summary + Export */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          {[
            { label: 'Total Sessions',   value: sessions.length   },
            { label: 'Submitted',        value: submitted.length  },
            { label: 'In Progress',      value: inProgress.length },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 16px', minWidth: 100 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text1)' }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
        <button className="btn btn-primary btn-sm" onClick={exportCSV} disabled={exporting || sessions.length === 0}>
          {exporting ? 'Exporting...' : '⬇ Export CSV'}
        </button>
      </div>

      {sessions.length === 0 ? (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>
          No UAT sessions recorded yet. Sessions will appear here once testers begin.
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Tester</th>
                <th>Track</th>
                <th>Status</th>
                <th>Results</th>
                <th>Rating</th>
                <th>Started</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map(s => {
                const total   = (s.Passed || 0) + (s.Failed || 0) + (s.Partial || 0);
                const passRate = total > 0 ? Math.round((s.Passed / total) * 100) : null;
                return (
                  <tr key={s.SessionID}>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{s.TesterName}</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{s.TesterEmail}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{s.TesterRole}</div>
                    </td>
                    <td>
                      <span className={`badge ${s.Track === 'admin' ? 'badge-info' : 'badge-neutral'}`}>
                        {s.Track === 'admin' ? 'Admin' : 'User'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${s.Status === 'submitted' ? 'badge-success' : 'badge-neutral'}`}>
                        {s.Status === 'submitted' ? 'Submitted' : 'In Progress'}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: 12, display: 'flex', gap: 8 }}>
                        <span style={{ color: '#16a34a', fontWeight: 600 }}>✓ {s.Passed || 0}</span>
                        <span style={{ color: '#ca8a04', fontWeight: 600 }}>~ {s.Partial || 0}</span>
                        <span style={{ color: '#dc2626', fontWeight: 600 }}>✗ {s.Failed || 0}</span>
                      </div>
                      {passRate !== null && (
                        <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>{passRate}% pass rate</div>
                      )}
                    </td>
                    <td>
                      {s.OverallRating ? (
                        <span style={{ color: '#f59e0b', fontWeight: 600 }}>{'★'.repeat(s.OverallRating)}{'☆'.repeat(5 - s.OverallRating)}</span>
                      ) : <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{formatDateTime(s.StartedAt)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{formatDateTime(s.SubmittedAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Admin Page ───────────────────────────────────────────
export function AdminPage() {
  const { isAdmin, isApprover } = useAuth();
  const [activeTab, setActiveTab] = useState('departments');
  const [toast, setToast]         = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
  }, []);

  if (!isAdmin && !isApprover) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text1)', marginBottom: 6 }}>Access Restricted</div>
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>Admin Settings is only available to IT Admins and IT Approvers.</div>
      </div>
    );
  }

  const canEdit = isAdmin; // IT.Approver gets full read visibility below, never write access

  const tabs = [
    { id: 'departments', label: 'Departments'  },
    { id: 'users',       label: 'Users & Roles' },
    { id: 'projects',    label: 'Projects'      },
    { id: 'settings',   label: 'Settings'       },
  ];

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="page-header">
        <div>
          <h1 className="page-title">Admin</h1>
          <p className="page-sub">Manage departments, users, projects, and portal configuration</p>
        </div>
      </div>
      {!canEdit && (
        <div style={{ padding: '10px 14px', background: 'var(--info-bg, #eff6ff)', border: '1px solid var(--accent)', borderRadius: 8, marginBottom: 16, fontSize: 13, color: 'var(--accent)' }}>
          👁 Viewing as IT Approver — read-only. Changes require an IT Admin.
        </div>
      )}
      <StatsHeader />
      <div className="card" style={{ padding: '0 20px' }}>
        <TabBar tabs={tabs} active={activeTab} onChange={setActiveTab} />
        <div style={{ paddingBottom: 20 }}>
          {activeTab === 'departments' && <DepartmentsTab showToast={showToast} canEdit={canEdit} />}
          {activeTab === 'users'       && <UsersTab       showToast={showToast} canEdit={canEdit} />}
          {activeTab === 'projects'    && <ProjectsTab    showToast={showToast} canEdit={canEdit} />}
          {activeTab === 'settings'   && <SettingsTab     showToast={showToast} canEdit={canEdit} />}
        </div>
      </div>
    </div>
  );
}
